import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Package,
  FolderTree,
  LayoutTemplate,
  Tag,
  FileCheck,
  ClipboardList,
  LayoutList,
  ClipboardCheck,
  ShieldCheck,
  Shield,
  ArrowLeftRight,
  Repeat,
  Shuffle,
  AlertTriangle,
  Users,
  MailPlus,
  BellRing,
  Mail,
  User,
  UserCog,
  Building,
  Languages,
  Paperclip,
  Table,
  Handshake,
  Key,
  ListOrdered,
  Webhook,
  Banknote,
  Grid3X3,
  Bug,
  FlaskConical,
  FlaskRound,
  Receipt,
  ScanSearch,
  Building2,
  Home,
  Lock,
  type LucideIcon,
} from "lucide-react";
import { Button } from "@/shared/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/shared/components/ui/dialog";
import { SearchInput } from "@/shared/components/ui/search-input";
import { cn } from "@/shared/lib/utils";
import {
  groupPermissionsByCategory,
  buildMatrixLayout,
  EXTERNAL_ROLE_FLOOR_CODENAMES,
  type Permission,
  type PermissionModule,
  type PermState,
} from "@/admin/lib/permissions";
import { PermissionMatrix } from "@/admin/pages/settings/permission-matrix";

// ---------------------------------------------------------------------------
// Module icon lookup
// ---------------------------------------------------------------------------

const MODULE_ICONS: Record<string, LucideIcon> = {
  products: Package,
  "product-categories": FolderTree,
  "product-templates": LayoutTemplate,
  "product-brands": Tag,
  holdings: FileCheck,
  claims: ClipboardList,
  "claim-schemas": LayoutList,
  "claim-workflows": ClipboardList,
  inspections: ClipboardCheck,
  warranties: ShieldCheck,
  "warranty-templates": Shield,
  "warranty-mappings": ArrowLeftRight,
  "warranty-options": Shield,
  transfers: Repeat,
  workflows: Shuffle,
  exceptions: AlertTriangle,
  customers: Users,
  "email-triggers": MailPlus,
  reminders: BellRing,
  templates: Mail,
  users: User,
  roles: UserCog,
  organisation: Building,
  translations: Languages,
  attachments: Paperclip,
  "table-views": Table,
  partnerships: Handshake,
  "api-keys": Key,
  "api-logs": ListOrdered,
  webhooks: Webhook,
  "claim-payment": Banknote,
  "decision-matrix": Grid3X3,
  fault: Bug,
  "lab-analysis": FlaskConical,
  "lab-request": FlaskRound,
  "settlement-line-item": Receipt,
  "evidence-annotation": ScanSearch,
  structure: Building2,
  property: Home,
};

function permTypeFromCodename(codename: string): string {
  const action = codename.substring(codename.indexOf(".") + 1);
  if (
    action.includes("view") ||
    action.includes("create") ||
    action.includes("edit") ||
    action.includes("delete") ||
    action === "manage" ||
    action === "manage-defaults"
  )
    return "crud";
  if (
    action.includes("approve") ||
    action.includes("execute") ||
    action.includes("elevated") ||
    action.includes("request") ||
    action.includes("deny") ||
    action.includes("cancel") ||
    action.includes("hold") ||
    action.includes("upload")
  )
    return "workflow";
  return "special";
}

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface PermissionEditorModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  roleName: string;
  roleDescription: string;
  isAdmin: boolean;
  allPermissions: Permission[];
  grantedCodenames: Set<string>;
  onToggle: (codename: string | string[]) => void;
  saving: boolean;
  /**
   * External-safe mode: restrict the editor to permissions flagged
   * `is_suitable_for_external_assignment`. Used by the External Roles tab so
   * partner roles can only ever be granted external-safe codenames (mirrors the
   * backend `limit_choices_to` guard on `ExternalOrganisationRole.permissions`).
   */
  externalSafeOnly?: boolean;
  /**
   * Whether the current actor holds `external-roles.grant_unsafe`. When true, in
   * external-safe mode not-recommended (non-floor) permissions become selectable
   * behind a per-permission confirmation, instead of being locked. Floor
   * codenames stay locked regardless. Ignored outside `externalSafeOnly`.
   */
  canGrantUnsafe?: boolean;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function PermissionEditorModal({
  open,
  onOpenChange,
  roleName,
  roleDescription,
  isAdmin,
  allPermissions,
  grantedCodenames,
  onToggle,
  saving,
  externalSafeOnly = false,
  canGrantUnsafe = false,
}: PermissionEditorModalProps) {
  const { t } = useTranslation();
  const [search, setSearch] = useState("");
  const [view, setView] = useState<"list" | "matrix">("list");
  const [activeModule, setActiveModule] = useState<string | null>(null);
  // External-safe mode defaults to hiding not-recommended codenames. The user
  // can reveal them: floor codenames stay locked; not-recommended (non-floor)
  // codenames become selectable behind a confirmation when the actor holds
  // `external-roles.grant_unsafe`, otherwise they too stay locked.
  const [showInternalOnly, setShowInternalOnly] = useState(false);
  // The not-recommended permission the user is confirming a deliberate grant of.
  const [overrideConfirm, setOverrideConfirm] = useState<Permission | null>(
    null,
  );

  // Classify a permission for the role being edited. See `PermState`.
  const permState = useCallback(
    (p: Permission): PermState => {
      if (!externalSafeOnly || p.is_suitable_for_external_assignment)
        return "grantable";
      if (EXTERNAL_ROLE_FLOOR_CODENAMES.has(p.codename)) return "locked";
      return canGrantUnsafe ? "override" : "locked";
    },
    [externalSafeOnly, canGrantUnsafe],
  );

  // Permissions that can actually be granted on this role. Counters and the
  // "grant all" logic are always relative to this set, never to the reference-
  // only rows the user may have revealed.
  const grantablePermissions = useMemo(
    () =>
      externalSafeOnly
        ? allPermissions.filter((p) => p.is_suitable_for_external_assignment)
        : allPermissions,
    [allPermissions, externalSafeOnly],
  );

  // What the editor displays: the grantable set by default, plus internal-only
  // rows (locked) once the user opts to reveal them.
  const effectivePermissions = useMemo(
    () =>
      externalSafeOnly && !showInternalOnly
        ? grantablePermissions
        : allPermissions,
    [allPermissions, externalSafeOnly, showInternalOnly, grantablePermissions],
  );

  const categories = useMemo(
    () => groupPermissionsByCategory(effectivePermissions),
    [effectivePermissions],
  );

  const filteredCategories = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return categories;

    return categories
      .map((cat) => ({
        ...cat,
        modules: cat.modules
          .map((mod) => ({
            ...mod,
            permissions: mod.permissions.filter(
              (p) =>
                p.name.toLowerCase().includes(q) ||
                p.codename.toLowerCase().includes(q),
            ),
          }))
          .filter((mod) => mod.permissions.length > 0),
      }))
      .filter((cat) => cat.modules.length > 0);
  }, [categories, search]);

  const visibleModuleKeys = useMemo(
    () =>
      new Set(filteredCategories.flatMap((c) => c.modules.map((m) => m.key))),
    [filteredCategories],
  );

  const resolvedActiveModule = useMemo(() => {
    if (activeModule && visibleModuleKeys.has(activeModule))
      return activeModule;
    const first = filteredCategories[0]?.modules[0]?.key;
    return first ?? null;
  }, [activeModule, visibleModuleKeys, filteredCategories]);

  const currentModule = useMemo(() => {
    if (!resolvedActiveModule) return null;
    for (const cat of filteredCategories) {
      const found = cat.modules.find((m) => m.key === resolvedActiveModule);
      if (found) return found;
    }
    return null;
  }, [filteredCategories, resolvedActiveModule]);

  const totalGranted = useMemo(() => {
    let count = 0;
    for (const p of grantablePermissions) {
      if (grantedCodenames.has(p.codename)) count++;
    }
    return count;
  }, [grantablePermissions, grantedCodenames]);

  // Matrix (comparison) view: module x action grid derived from whatever the
  // list view is currently showing, so both views stay in lockstep with the
  // external-safe reveal toggle.
  const matrixRows = useMemo(
    () => buildMatrixLayout(effectivePermissions).rows,
    [effectivePermissions],
  );

  const permByCodename = useMemo(() => {
    const map = new Map<string, Permission>();
    for (const p of allPermissions) map.set(p.codename, p);
    return map;
  }, [allPermissions]);

  // Shared state resolution + activation so the matrix honours the exact same
  // grantable / override / locked semantics as the list rows.
  const stateForCodename = useCallback(
    (codename: string): PermState => {
      const p = permByCodename.get(codename);
      return p ? permState(p) : "grantable";
    },
    [permByCodename, permState],
  );

  const activateCodename = useCallback(
    (codename: string) => {
      const p = permByCodename.get(codename);
      if (!p) return;
      const state = permState(p);
      if (state === "locked") return;
      const isGranted = grantedCodenames.has(codename);
      // A not-recommended grant is confirmed first; revoking one and every
      // recommended toggle applies immediately.
      if (state === "override" && !isGranted) setOverrideConfirm(p);
      else onToggle(codename);
    },
    [permByCodename, permState, grantedCodenames, onToggle],
  );

  const handleToggleAll = useCallback(
    (mod: PermissionModule) => {
      if (isAdmin || saving) return;
      const q = search.trim().toLowerCase();
      // Bulk grant/revoke only ever touches freely-grantable (recommended)
      // permissions. Not-recommended overrides are deliberate, per-write grants
      // and must go through the individual confirmation, never a "Grant all".
      const perms = (
        q
          ? mod.permissions.filter(
              (p) =>
                p.name.toLowerCase().includes(q) ||
                p.codename.toLowerCase().includes(q),
            )
          : mod.permissions
      ).filter((p) => permState(p) === "grantable");
      if (perms.length === 0) return;
      const allGranted = perms.every((p) => grantedCodenames.has(p.codename));
      const toToggle: string[] = [];
      for (const p of perms) {
        const isGranted = grantedCodenames.has(p.codename);
        if (allGranted && isGranted) toToggle.push(p.codename);
        if (!allGranted && !isGranted) toToggle.push(p.codename);
      }
      if (toToggle.length > 0) onToggle(toToggle);
    },
    [isAdmin, saving, search, grantedCodenames, onToggle, permState],
  );

  const handleOpenChange = useCallback(
    (next: boolean) => {
      if (!next) {
        setSearch("");
        setActiveModule(null);
        setShowInternalOnly(false);
        setView("list");
      }
      onOpenChange(next);
    },
    [onOpenChange],
  );

  return (
    <>
      <Dialog open={open} onOpenChange={handleOpenChange}>
        <DialogContent
          bare
          showCloseButton
          className="mx-4 flex max-h-[min(640px,85vh)] w-full max-w-3xl flex-col sm:max-w-3xl"
        >
          {/* Header */}
          <div className="flex items-center gap-3 border-b-[0.5px] border-b-[var(--rule)] px-6 pt-5 pb-4">
            <ShieldCheck className="h-5 w-5 text-[var(--green)]" />
            <div className="min-w-0 flex-1">
              <h2 className="text-[15px] font-medium text-[var(--ink)]">
                {roleName}
              </h2>
              {roleDescription && (
                <p className="truncate text-[11px] text-[var(--ink-3)]">
                  {roleDescription}
                </p>
              )}
            </div>
            <span className="rounded-md bg-[var(--green-bg)] px-3 py-1 text-[12px] font-medium text-[var(--green)]">
              {totalGranted} / {grantablePermissions.length}
            </span>
          </div>

          {externalSafeOnly && (
            <div className="flex items-center justify-between gap-4 border-b-[0.5px] border-b-[var(--rule)] bg-[color:var(--amber-bg,#FAEEDA)] px-6 py-2.5">
              <p className="text-[11px] leading-snug text-[color:var(--amber-ink,#633806)]">
                {!showInternalOnly
                  ? t(
                      "externalRoles.externalSafeOnlyNotice",
                      "Only permissions recommended for partner roles are shown. Reveal the rest to grant a not-recommended permission with caution.",
                    )
                  : canGrantUnsafe
                    ? t(
                        "externalRoles.externalSafeOverrideShownNotice",
                        "Not-recommended permissions can be granted to this partner role with caution - each grant asks you to confirm. A few internal-only permissions stay locked and can never be granted.",
                      )
                    : t(
                        "externalRoles.externalSafeAllShownNotice",
                        "Not-recommended permissions are shown for reference and cannot be granted to partner roles.",
                      )}
              </p>
              <Button
                type="button"
                variant="ghost"
                onClick={() => setShowInternalOnly((v) => !v)}
                className="h-auto shrink-0 cursor-pointer px-2 py-1 text-[11px] font-medium text-[color:var(--amber-ink,#633806)] hover:bg-[color:var(--amber-bg-hover,#F4E0BE)]"
              >
                {showInternalOnly
                  ? t("externalRoles.showSafeOnly", "Show external-safe only")
                  : t(
                      "externalRoles.showAllPermissions",
                      "Show all permissions",
                    )}
              </Button>
            </div>
          )}

          {/* Toolbar: view toggle + search */}
          <div className="flex items-center gap-3 border-b-[0.5px] border-b-[var(--rule)] px-6 py-3">
            <div className="flex shrink-0 items-center rounded-[6px] border-[0.5px] border-[var(--rule)] bg-[var(--bg-subtle)] p-0.5">
              <Button
                type="button"
                variant="ghost"
                onClick={() => setView("list")}
                className={cn(
                  "h-7 gap-1.5 rounded-[4px] border-[0.5px] px-2.5 text-[11px] font-medium",
                  view === "list"
                    ? "border-[var(--rule-strong)] bg-[var(--bg)] text-[var(--ink)]"
                    : "border-transparent text-[var(--ink-3)] hover:text-[var(--ink)]",
                )}
              >
                <LayoutList className="h-3.5 w-3.5" />
                {t("roles.viewList", "List")}
              </Button>
              <Button
                type="button"
                variant="ghost"
                onClick={() => setView("matrix")}
                className={cn(
                  "h-7 gap-1.5 rounded-[4px] border-[0.5px] px-2.5 text-[11px] font-medium",
                  view === "matrix"
                    ? "border-[var(--rule-strong)] bg-[var(--bg)] text-[var(--ink)]"
                    : "border-transparent text-[var(--ink-3)] hover:text-[var(--ink)]",
                )}
              >
                <Grid3X3 className="h-3.5 w-3.5" />
                {t("roles.viewMatrix", "Matrix")}
              </Button>
            </div>
            {view === "list" ? (
              <SearchInput
                value={search}
                onChange={setSearch}
                placeholder="Search permissions by name or codename..."
                className="h-9 flex-1"
              />
            ) : (
              <p className="flex-1 text-[11px] text-[var(--ink-3)]">
                {t(
                  "roles.matrixViewDescription",
                  "CRUD overview across modules. Switch to List for workflow and special permissions.",
                )}
              </p>
            )}
          </div>

          {view === "matrix" ? (
            /* Matrix (comparison) view */
            <div className="min-h-0 flex-1 overflow-auto px-6 py-4">
              {matrixRows.length === 0 ? (
                <p className="py-12 text-center text-[12px] text-[var(--ink-3)]">
                  {t("roles.noPermissionsToCompare", "No permissions to compare.")}
                </p>
              ) : (
                <PermissionMatrix
                  rows={matrixRows}
                  grantedCodenames={grantedCodenames}
                  readOnly={isAdmin}
                  saving={saving}
                  stateForCodename={stateForCodename}
                  onActivate={activateCodename}
                />
              )}
            </div>
          ) : (
            /* Body: sidebar + detail */
            <div className="grid min-h-0 flex-1 grid-cols-[220px_1fr] overflow-hidden">
              {/* Sidebar */}
              <nav className="overflow-y-auto border-r-[0.5px] border-r-[var(--rule)] bg-[var(--bg-subtle)]">
                {filteredCategories.length === 0 ? (
                  <p className="px-4 py-8 text-center text-[11px] text-[var(--ink-3)]">
                    No permissions match
                  </p>
                ) : (
                  filteredCategories.map((cat) => (
                    <div key={cat.category}>
                      <div className="px-4 pt-3 pb-1 text-[9px] font-semibold uppercase tracking-[0.06em] text-[var(--ink-3)]">
                        {cat.category}
                      </div>
                      {cat.modules.map((mod) => {
                        const isActive = resolvedActiveModule === mod.key;
                        // Sidebar counts mirror the detail panel: recommended
                        // (freely-grantable) permissions only. Not-recommended
                        // overrides are surfaced per-row, not in the tally.
                        const grantableRows = mod.permissions.filter(
                          (p) => permState(p) === "grantable",
                        );
                        const grantedInMod = grantableRows.filter((p) =>
                          grantedCodenames.has(p.codename),
                        ).length;
                        const grantableInMod = grantableRows.length;
                        const Icon = MODULE_ICONS[mod.key] ?? Package;
                        return (
                          <Button
                            key={mod.key}
                            variant="ghost"
                            size="sm"
                            onClick={() => setActiveModule(mod.key)}
                            className={cn(
                              "flex h-auto w-full items-center justify-start gap-2 rounded-none border-l-2 border-l-transparent px-4 py-2 text-left text-[12px] text-[var(--ink-2)] transition-colors duration-75",
                              isActive
                                ? "border-l-[var(--green)] bg-[var(--green-bg-muted)] font-medium text-[var(--green)]"
                                : "hover:bg-[var(--bg-hover)] hover:text-[var(--ink)]",
                            )}
                          >
                            <Icon className="h-3.5 w-3.5 shrink-0 opacity-60" />
                            <span className="min-w-0 flex-1 truncate">
                              {mod.label}
                            </span>
                            <span
                              className={cn(
                                "shrink-0 rounded-md border-[0.5px] px-1.5 py-px text-[10px] tabular-nums",
                                isActive
                                  ? "border-transparent bg-[var(--green-bg)] text-[var(--green)]"
                                  : "border-[var(--rule)] bg-transparent text-[var(--ink-3)]",
                              )}
                            >
                              {grantedInMod}/{grantableInMod}
                            </span>
                          </Button>
                        );
                      })}
                    </div>
                  ))
                )}
              </nav>

              {/* Detail panel */}
              <div className="overflow-y-auto px-6 py-4">
                {currentModule ? (
                  <ModuleDetail
                    mod={currentModule}
                    grantedCodenames={grantedCodenames}
                    isAdmin={isAdmin}
                    saving={saving}
                    search={search}
                    onToggle={onToggle}
                    onToggleAll={handleToggleAll}
                    permState={permState}
                    onOverrideGrantRequest={setOverrideConfirm}
                  />
                ) : (
                  <p className="py-12 text-center text-[12px] text-[var(--ink-3)]">
                    No permissions match your search.
                  </p>
                )}
              </div>
            </div>
          )}

          {/* Footer */}
          <DialogFooter className="flex items-center justify-between gap-3 border-t-[0.5px] border-t-[var(--rule)] bg-[var(--bg-subtle)] px-6 py-3 sm:justify-between">
            <span className="text-[11px] tabular-nums text-[var(--ink-3)]">
              <span className="mr-1.5 inline-block h-2 w-2 rounded-full bg-[var(--green)]" />
              {totalGranted} granted
              <span className="mx-3 inline-block h-2 w-2 rounded-full bg-[var(--rule-strong)]" />
              {grantablePermissions.length - totalGranted} not granted
            </span>
            <Button
              type="button"
              variant="outline"
              onClick={() => handleOpenChange(false)}
              className="cursor-pointer px-4 py-2 text-[11px] uppercase tracking-[0.1em] text-[var(--ink-3)]"
            >
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Per-permission confirmation before a deliberate not-recommended grant. */}
      <Dialog
        open={overrideConfirm !== null}
        onOpenChange={(next) => {
          if (!next) setOverrideConfirm(null);
        }}
      >
        <DialogContent className="sm:max-w-md" showCloseButton={false}>
          <DialogHeader className="flex-row items-start gap-3 space-y-0">
            <span className="mt-0.5 inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[color:var(--amber-bg,#FAEEDA)] text-[color:var(--amber-ink,#633806)]">
              <AlertTriangle className="h-4 w-4" />
            </span>
            <div className="flex min-w-0 flex-col gap-1.5">
              <DialogTitle className="font-sans text-[15px] leading-snug font-medium text-[var(--ink)]">
                {t(
                  "externalRoles.overrideConfirmTitle",
                  "Grant a not-recommended permission?",
                )}
              </DialogTitle>
              <DialogDescription className="text-[13px] leading-[1.5] text-[var(--ink-2)]">
                {t(
                  "externalRoles.overrideConfirmBody",
                  "This permission is not recommended for partner roles. Granting it gives the partner organisation a capability that is normally kept internal. Only proceed if this partner genuinely needs it.",
                )}
              </DialogDescription>
            </div>
          </DialogHeader>
          {overrideConfirm && (
            <div className="rounded-md border-[0.5px] border-[var(--rule)] bg-[var(--bg-subtle)] px-3 py-2">
              <p className="text-[13px] text-[var(--ink)]">
                {overrideConfirm.name}
              </p>
              <p className="mt-0.5 font-mono text-[11px] text-[var(--ink-3)]">
                {overrideConfirm.codename}
              </p>
            </div>
          )}
          <DialogFooter className="border-t-[0.5px] border-t-[var(--rule)]">
            <Button
              type="button"
              variant="outline"
              onClick={() => setOverrideConfirm(null)}
            >
              {t("common.cancel", "Cancel")}
            </Button>
            <Button
              type="button"
              disabled={saving}
              onClick={() => {
                if (overrideConfirm) onToggle(overrideConfirm.codename);
                setOverrideConfirm(null);
              }}
              className="bg-[color:var(--amber-ink,#633806)] text-[var(--bg)] hover:bg-[color:var(--amber-ink,#633806)]/90"
            >
              {t("externalRoles.overrideConfirmAction", "Grant anyway")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

// ---------------------------------------------------------------------------
// Module detail sub-component
// ---------------------------------------------------------------------------

function ModuleDetail({
  mod,
  grantedCodenames,
  isAdmin,
  saving,
  search,
  onToggle,
  onToggleAll,
  permState,
  onOverrideGrantRequest,
}: {
  mod: PermissionModule;
  grantedCodenames: Set<string>;
  isAdmin: boolean;
  saving: boolean;
  search: string;
  onToggle: (codename: string) => void;
  onToggleAll: (mod: PermissionModule) => void;
  permState: (p: Permission) => PermState;
  onOverrideGrantRequest: (p: Permission) => void;
}) {
  const { t } = useTranslation();
  const q = search.trim().toLowerCase();
  const perms = q
    ? mod.permissions.filter(
        (p) =>
          p.name.toLowerCase().includes(q) ||
          p.codename.toLowerCase().includes(q),
      )
    : mod.permissions;
  // Counts and the grant-all affordance are scoped to freely-grantable
  // (recommended) permissions; locked and not-recommended-override rows are
  // handled individually.
  const grantablePerms = perms.filter((p) => permState(p) === "grantable");
  const grantedCount = grantablePerms.filter((p) =>
    grantedCodenames.has(p.codename),
  ).length;
  const allGranted =
    grantablePerms.length > 0 && grantedCount === grantablePerms.length;

  const Icon = MODULE_ICONS[mod.key] ?? Package;

  return (
    <>
      <div className="mb-4 flex items-center gap-3 border-b-[0.5px] border-b-[var(--rule)] pb-4">
        <Icon className="h-[18px] w-[18px] text-[var(--green)]" />
        <div className="min-w-0 flex-1">
          <p className="text-[14px] font-medium text-[var(--ink)]">
            {mod.label}
          </p>
          <p className="text-[11px] text-[var(--ink-3)]">
            {grantedCount} of {grantablePerms.length} permissions granted
          </p>
        </div>
        {!isAdmin && grantablePerms.length > 0 && (
          <Button
            type="button"
            variant="ghost"
            onClick={() => onToggleAll(mod)}
            disabled={saving}
            className="h-auto cursor-pointer px-3 py-1.5 text-[11px] font-medium text-[var(--green)] hover:bg-[var(--green-bg)]"
          >
            {allGranted ? "Revoke all" : "Grant all"}
          </Button>
        )}
      </div>

      {perms.map((p) => {
        const state = permState(p);
        const locked = state === "locked";
        const override = state === "override";
        const isGranted = grantedCodenames.has(p.codename);
        const clickable = !isAdmin && !saving && !locked;
        const pType = permTypeFromCodename(p.codename);
        // A not-recommended grant is confirmed first; revoking one, and every
        // recommended toggle, applies immediately.
        const handleClick = () => {
          if (!clickable) return;
          if (override && !isGranted) onOverrideGrantRequest(p);
          else onToggle(p.codename);
        };
        return (
          <Button
            key={p.codename}
            variant="ghost"
            disabled={!clickable}
            onClick={handleClick}
            title={
              locked
                ? t(
                    "externalRoles.internalOnlyTooltip",
                    "Internal-only permission. It cannot be granted to a partner role.",
                  )
                : override
                  ? t(
                      "externalRoles.notRecommendedTooltip",
                      "Not recommended for partner roles. You can grant it deliberately - we will ask you to confirm.",
                    )
                  : undefined
            }
            className={cn(
              "group flex h-auto w-full items-center justify-start gap-3 rounded-none border-b-[0.5px] border-b-[var(--rule-light)] px-0 py-2.5 text-left transition-colors duration-75 last:border-b-0",
              clickable
                ? "cursor-pointer hover:bg-[var(--bg-subtle)] hover:-mx-2 hover:px-2 hover:rounded-md"
                : "cursor-not-allowed",
              locked && "opacity-60",
            )}
          >
            {locked ? (
              <span className="inline-flex h-[17px] w-[17px] shrink-0 items-center justify-center rounded-[3px] border border-[var(--rule-strong)] bg-[var(--bg-subtle)] text-[var(--ink-3)]">
                <Lock className="h-2.5 w-2.5" />
              </span>
            ) : override ? (
              <span
                className={cn(
                  "inline-flex h-[17px] w-[17px] shrink-0 items-center justify-center rounded-[3px] border transition-all duration-150",
                  isGranted
                    ? "border-[color:var(--amber-ink,#633806)] bg-[color:var(--amber-ink,#633806)] text-[var(--bg)]"
                    : "border-[color:var(--amber-ink,#633806)] bg-transparent text-transparent",
                )}
              >
                {isGranted && (
                  <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
                    <path
                      d="M2 5L4 7L8 3"
                      stroke="#fff"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                )}
              </span>
            ) : (
              <span
                className={cn(
                  "inline-flex h-[17px] w-[17px] shrink-0 items-center justify-center rounded-[3px] border transition-all duration-150",
                  isGranted
                    ? "border-[var(--green)] bg-[var(--green)] text-[var(--bg)] group-hover:border-[var(--green-hover)] group-hover:bg-[var(--green-hover)]"
                    : "border-[var(--rule-strong)] bg-transparent text-transparent group-hover:border-[var(--green)]",
                )}
              >
                {isGranted && (
                  <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
                    <path
                      d="M2 5L4 7L8 3"
                      stroke="#fff"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                )}
              </span>
            )}
            <span className="min-w-0 flex-1">
              <span className="block text-[13px] leading-tight text-[var(--ink)]">
                <Highlight text={p.name} query={search} />
              </span>
              <span className="mt-1 block font-mono text-[10px] leading-tight text-[var(--ink-3)]">
                <Highlight text={p.codename} query={search} />
              </span>
            </span>
            {locked ? (
              <span className="shrink-0 rounded-[3px] bg-[var(--bg-subtle)] px-2 py-0.5 text-[9px] font-semibold uppercase text-[var(--ink-3)]">
                {t("externalRoles.internalOnlyBadge", "Internal only")}
              </span>
            ) : override ? (
              <span className="flex shrink-0 items-center gap-1 rounded-[3px] bg-[color:var(--amber-bg,#FAEEDA)] px-2 py-0.5 text-[9px] font-semibold uppercase text-[color:var(--amber-ink,#633806)]">
                <AlertTriangle className="h-2.5 w-2.5" />
                {t("externalRoles.notRecommendedBadge", "Not recommended")}
              </span>
            ) : (
              <span
                className={cn(
                  "shrink-0 rounded-[3px] px-2 py-0.5 text-[9px] font-semibold uppercase",
                  pType === "crud" &&
                    "bg-[var(--green-bg)] text-[var(--green)]",
                  pType === "workflow" &&
                    "bg-[var(--green-bg)] text-[var(--green)]",
                  pType === "special" &&
                    "bg-[color:var(--amber-bg,#FAEEDA)] text-[color:var(--amber-ink,#633806)]",
                )}
              >
                {pType}
              </span>
            )}
          </Button>
        );
      })}
    </>
  );
}

// ---------------------------------------------------------------------------
// Search highlight
// ---------------------------------------------------------------------------

function Highlight({ text, query }: { text: string; query: string }) {
  const q = query.trim().toLowerCase();
  if (!q) return <>{text}</>;
  const idx = text.toLowerCase().indexOf(q);
  if (idx < 0) return <>{text}</>;
  return (
    <>
      {text.substring(0, idx)}
      <mark className="rounded-sm bg-[#FAC775] px-px">
        {text.substring(idx, idx + q.length)}
      </mark>
      {text.substring(idx + q.length)}
    </>
  );
}
